function getBrincatoysUnrepresentedFilters(forms, currentSearch) {
  const represented = new Map();
  forms.forEach((form) => form.querySelectorAll('[name^="filter."]').forEach((input) => {
    // Price inputs represent a boundary even after it has been cleared. Lists
    // represent individual values: Shopify may omit only the selected values.
    if (input.name.startsWith('filter.v.price.') || typeof input.value !== 'string') {
      represented.set(input.name, null);
    } else if (represented.get(input.name) !== null) {
      if (!represented.has(input.name)) represented.set(input.name, new Set());
      represented.get(input.name).add(input.value);
    }
  }));
  const missing = new Map();
  for (const [name, value] of new URLSearchParams(currentSearch)) {
    if (!name.startsWith('filter.') || !value.trim()) continue;
    if (represented.has(name) && (represented.get(name) === null || represented.get(name).has(value))) continue;
    if (!missing.has(name)) missing.set(name, []);
    if (!missing.get(name).includes(value)) missing.get(name).push(value);
  }
  return missing;
}

function buildBrincatoysFacetSearchParams(forms, preferredSort, currentSearch = '', changedInput) {
  const searchParams = new URLSearchParams();

  forms.forEach((form) => {
    const formData = new FormData(form);

    for (const [key, formValue] of formData.entries()) {
      const value = typeof formValue === 'string' && key !== 'filter.p.product_type'
        ? formValue.trim()
        : formValue;
      if (typeof value === 'string' && value.trim() === '') continue;

      if (key.startsWith('filter.')) {
        const existingValues = searchParams.getAll(key);
        if (!existingValues.includes(String(value))) searchParams.append(key, value);
      } else {
        searchParams.set(key, value);
      }
    }
  });

  // Shopify omits list facets when no products match. Their absence must not
  // silently clear an earlier choice when the customer edits the budget or sort.
  if (currentSearch) {
    getBrincatoysUnrepresentedFilters(forms, currentSearch).forEach((values, name) => {
      values.forEach((value) => searchParams.append(name, value));
    });
  }

  if (preferredSort) searchParams.set('sort_by', preferredSort);

  // Unchecking a visible age clears its omitted aliases as well. Other edits
  // retain those aliases, so recovering a zero result cannot narrow the age.
  if (changedInput?.matches?.('[data-bt-age-control]') && !changedInput.checked) {
    const member = changedInput.closest('[data-bt-age-group]')?.querySelector('[data-bt-age-member]');
    const ageKey = getBrincatoysAgeKey(member?.value);
    if (ageKey) {
      const name = 'filter.p.product_type';
      const remaining = searchParams.getAll(name).filter(value => getBrincatoysAgeKey(value) !== ageKey);
      searchParams.delete(name);
      remaining.forEach(value => searchParams.append(name, value));
    }
  }

  return searchParams.toString();
}

function markBrincatoysPartialAgeGroups(root) {
  root.querySelectorAll('[data-bt-age-partial] [data-bt-age-control]').forEach((input) => {
    input.indeterminate = true;
  });
}

function getBrincatoysAgeKey(value) {
  // Keep this key consistent with snippets/brincatoys-valid-age.liquid.
  // Other filters can remove selected aliases from Shopify's active_values response.
  const label = String(value ?? '').trim().toLowerCase();
  const parts = label.replace(/-/g, ' ').replace(/\+/g, '').trim().split(/\s+/);
  const amount = Number(parts[0]);
  if (!Number.isFinite(amount) || String(amount) !== parts[0]) return '';

  const valid = (parts.length === 2 && (label.includes('+') || parts[1] === 'plus'))
    || (parts.length === 3 && parts[2] === 'plus')
    || (parts.length === 3 && parts[1] === 'plus' && !label.startsWith('-'))
    || (parts.length === 4 && parts[2] === 'and' && ['up', 'over'].includes(parts[3]));
  if (!valid) return '';
  const unit = parts.length === 3 && parts[1] === 'plus' ? parts[2] : parts[1];

  if (['mês', 'meses', 'mes', 'month', 'months'].includes(unit)) {
    return `+ ${amount} ${amount === 1 ? 'mês' : 'meses'}`;
  }
  if (['ano', 'anos', 'año', 'años', 'year', 'years'].includes(unit) || (parts.length === 2 && parts[1] === 'plus')) {
    return amount >= 14 ? '__adults__' : `+ ${amount} ${amount === 1 ? 'ano' : 'anos'}`;
  }
  return '';
}

const brincatoysFacetChoiceLabels = new Map();

function rememberBrincatoysFacetLabels() {
  document.querySelectorAll('[data-bt-filter-name][data-bt-filter-value]').forEach((chip) => {
    brincatoysFacetChoiceLabels.set(`${chip.dataset.btFilterName}\n${chip.dataset.btFilterValue}`, {
      filter: chip.dataset.btFilterLabel,
      value: chip.dataset.btValueLabel,
    });
  });
}

function getBrincatoysMissingFilterChoices(forms, currentSearch, labels, knownLabels = new Map()) {
  const choices = [];
  const visibleActiveAges = new Set(forms.flatMap(form =>
    Array.from(form.querySelectorAll('[name^="filter."]'))
      .filter(input => input.name === 'filter.p.product_type' && input.checked)
      .map(input => getBrincatoysAgeKey(input.value))
      .filter(Boolean)
  ));
  getBrincatoysUnrepresentedFilters(forms, currentSearch).forEach((values, name) => {
    const groups = new Map();
    values.forEach((value) => {
      const ageKey = name === 'filter.p.product_type' ? getBrincatoysAgeKey(value) : '';
      const key = ageKey || value;
      if (!groups.has(key)) groups.set(key, {ageKey, values: []});
      groups.get(key).values.push(value);
    });
    groups.forEach((group) => {
      if (group.ageKey && visibleActiveAges.has(group.ageKey)) return;
      const value = group.values[0];
      const known = knownLabels.get(`${name}\n${value}`);
      const filterLabel = known?.filter || labels.filters[name] || labels.filter;
      let valueLabel = known?.value || value;
      if (group.ageKey) {
        if (group.ageKey === '__adults__') valueLabel = labels.adults;
        else {
          const amount = Number(group.ageKey.split(' ')[1]);
          const kind = group.ageKey.includes('mês') || group.ageKey.includes('meses') ? 'months' : 'years';
          valueLabel = labels.ages[`${kind}${amount === 1 ? 'One' : 'Other'}`].replace(/\b\d+\b/, String(amount));
        }
      } else if (!known && name !== 'filter.p.vendor' && name !== 'filter.p.product_type') {
        // Category IDs and other opaque values are not customer-facing labels.
        valueLabel = labels.selectedOne;
      }
      const removeParams = new URLSearchParams(currentSearch);
      const remaining = removeParams.getAll(name).filter((candidate) => !group.values.includes(candidate));
      removeParams.delete(name);
      remaining.forEach((candidate) => removeParams.append(name, candidate));
      choices.push({name, ...group, label: `${filterLabel}: ${valueLabel}`, removeQuery: removeParams.toString()});
    });
  });
  return choices;
}

function normalizeBrincatoysSearchPagination(root = document) {
  const current = new URLSearchParams(window.location.search);
  if (!current.has('q')) return;
  root.querySelectorAll('[data-bt-search-pagination][href]').forEach((link) => {
    const target = new URL(link.getAttribute('href'), window.location.href);
    target.searchParams.set('q', current.get('q'));
    for (const name of [...target.searchParams.keys()]) {
      if (name.startsWith('filter.')) target.searchParams.delete(name);
    }
    current.forEach((value, name) => {
      if (name.startsWith('filter.')) target.searchParams.append(name, value);
    });
    if (current.has('sort_by')) target.searchParams.set('sort_by', current.get('sort_by'));
    link.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);
  });
}

function updateBrincatoysSearchRecovery() {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('q');
  if (query === null) return;
  normalizeBrincatoysSearchPagination();

  // Shopify's translated HTML can encode an already-escaped search term. The
  // current query is authoritative, including literal text such as "&amp;".
  document.querySelectorAll('[data-bt-search-clear-filters], .bt-search-clear-filters').forEach((link) => {
    const target = new URL(link.getAttribute('href'), window.location.href);
    target.searchParams.set('q', query);
    link.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);
  });

  const grid = document.getElementById('product-grid');
  if (!grid?.hasAttribute('data-bt-search-has-facets')) return;
  const hasFilters = Array.from(params).some(([name, value]) => name.startsWith('filter.') && value.trim());
  const recovery = grid.querySelector('[data-bt-search-unfiltered-empty]');
  const filteredEmpty = grid.querySelector('[data-bt-search-filtered-empty]');
  if (recovery) recovery.hidden = hasFilters;
  if (filteredEmpty && recovery) filteredEmpty.hidden = !hasFilters;
  const cleanEmpty = Boolean(recovery) && !hasFilters;
  grid.closest('.template-search')?.classList.toggle('template-search--empty', cleanEmpty);
  document.querySelector('.bt-search-hero')?.classList.toggle('bt-search-hero--no-results', cleanEmpty);

  const aside = document.getElementById('main-search-filters');
  if (aside) {
    const showFilters = hasFilters || (!recovery && grid.dataset.btSearchHasFacets === 'true');
    const disabled = aside.dataset.btSearchFiltersEnabled === 'false';
    aside.hidden = !showFilters;
    aside.classList.toggle('facets-wrapper--no-filters', !showFilters || disabled);
    aside.closest('[data-bt-search-results-layout]')?.classList.toggle('facets--no-filters', !showFilters || disabled);
    if (aside.hidden && aside.contains(document.activeElement)) {
      const heading = document.querySelector('[data-bt-search-hero-copy] h1');
      heading?.setAttribute('tabindex', '-1');
      heading?.focus({preventScroll: true});
    }
  }
  if (recovery && hasFilters) {
    const copy = document.querySelector('[data-bt-search-hero-copy]');
    const heading = copy?.querySelector('h1');
    if (heading) heading.textContent = copy.dataset.resultsHeading;
    copy?.querySelector('.bt-search-hero__intro')?.setAttribute('hidden', '');
  }
}

function renderBrincatoysMissingFacets() {
  updateBrincatoysSearchRecovery();
  rememberBrincatoysFacetLabels();
  document.querySelectorAll('[data-bt-facet-labels]').forEach((data) => {
    const container = data.closest('.facets-container');
    const forms = Array.from(container.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile'));
    const labels = JSON.parse(data.textContent);
    const choices = getBrincatoysMissingFilterChoices(forms, window.location.search, labels, brincatoysFacetChoiceLabels);
    const template = container.querySelector('[data-bt-missing-filter-chip]');
    container.querySelectorAll('[data-bt-missing-facets]').forEach((list) => {
      list.replaceChildren();
      list.hidden = !choices.length;
      choices.forEach((choice) => {
        const chip = template.content.firstElementChild.cloneNode(true);
        chip.querySelector('[data-bt-choice-label]').textContent = choice.label;
        const link = chip.querySelector('a');
        link.setAttribute('href', `${window.location.pathname}${choice.removeQuery ? `?${choice.removeQuery}` : ''}`);
        if (choice.ageKey) {
          link.dataset.btAgeKey = choice.ageKey;
          link.dataset.btAgeValues = JSON.stringify(choice.values);
        }
        list.append(chip);
      });
    });
    const open = container.querySelector('.mobile-facets__open');
    if (open) {
      const count = Number(open.dataset.btServerCount || 0) + choices.length;
      let badge = open.querySelector('.bt-facets-active-count');
      if (count) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'bt-facets-active-count';
          open.querySelector('.mobile-facets__close').before(badge);
        }
        badge.textContent = String(count);
        badge.setAttribute('aria-label', (count === 1 ? labels.selectedOne : labels.selectedOther).replace(/\b\d+\b/, String(count)));
      } else badge?.remove();
    }
  });
}

function updateBrincatoysBudgetLinks() {
  const links = document.querySelectorAll('.bt-campaign--budget .bt-campaign__budgets a[href]');
  if (!links.length) return;

  const currentParams = new URLSearchParams(window.location.search);
  const retainedParams = new URLSearchParams();
  for (const [key, value] of currentParams) {
    // The destination collection defines the new budget. Keep the rest of the choice.
    const keepFilter = key.startsWith('filter.') && !key.startsWith('filter.v.price.');
    if ((!keepFilter && key !== 'sort_by') || !value.trim()) continue;
    if (!retainedParams.getAll(key).includes(value)) retainedParams.append(key, value);
  }
  links.forEach((link) => {
    const target = new URL(link.getAttribute('href'), window.location.href);
    if (target.origin !== window.location.origin) return;

    for (const key of [...target.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') target.searchParams.delete(key);
    }
    retainedParams.forEach((value, key) => target.searchParams.append(key, value));
    link.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);
  });
}

function getBrincatoysMobileFacetConsentReserve(bannerRect, viewportHeight, gap = 8) {
  if (
    !bannerRect
    || !Number.isFinite(bannerRect.top)
    || !Number.isFinite(bannerRect.height)
    || !Number.isFinite(bannerRect.width)
    || !Number.isFinite(viewportHeight)
    || viewportHeight <= 0
    || bannerRect.height <= 0
    || bannerRect.width <= 0
    || bannerRect.top >= viewportHeight
  ) return 0;

  return Math.max(0, Math.ceil(viewportHeight - Math.max(0, bannerRect.top) + gap));
}

if (!customElements.get('facet-filters-form')) {
  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

      this.debouncedOnSubmit = debounce((event, requestVersion) => {
        if (requestVersion !== FacetFiltersForm.requestVersion) return;
        FacetFiltersForm.pendingSubmission = false;
        this.onSubmitHandler(event);
      }, 800);

      const facetForm = this.querySelector('form');
      markBrincatoysPartialAgeGroups(facetForm);
      facetForm.addEventListener('input', (event) => {
        if (!event.target.matches?.('[data-bt-age-control]')) return;
        const group = event.target.closest('[data-bt-age-group]');
        group?.querySelectorAll('[data-bt-age-member]').forEach((input) => {
          input.checked = event.target.checked;
        });
      }, true);
      const scheduleSubmit = (event) => {
        // Reject the previous response now; serialize after the price slider's 600ms update.
        const requestVersion = FacetFiltersForm.invalidatePending();
        FacetFiltersForm.pendingSubmission = true;
        this.debouncedOnSubmit(event, requestVersion);
      };
      facetForm.addEventListener('input', scheduleSubmit);

      // Bind change for sort by dropdown
      facetForm.addEventListener('change', (event) => {
        if (event.target.tagName === 'SELECT' && event.target.classList.contains('facet-filters__sort')) {
          scheduleSubmit(event);
        }
      });

      const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
      if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    static setListeners() {
      const onHistoryChange = (event) => {
        const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
        updateBrincatoysBudgetLinks();
        if (searchParams === FacetFiltersForm.searchParamsPrev && !FacetFiltersForm.pendingSubmission) return;
        FacetFiltersForm.renderPage(searchParams, null, false);
      };
      window.addEventListener('popstate', onHistoryChange);
      document.addEventListener('click', (event) => {
        const retryButton = event.target.closest?.('[data-bt-facets-retry]');
        if (!retryButton) return;
        event.preventDefault();
        FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsPrev, event);
      });
    }

    static savedScrollY = 0;
    static restoredFromBFCache = false;
    static initBFCacheScrollHandling() {
      // Capture scroll position before leaving
      window.addEventListener('pagehide', () => {
        FacetFiltersForm.savedScrollY = window.scrollY;
      });

      // Restore scroll after bfcache restore
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        FacetFiltersForm.restoredFromBFCache = true;

        // Disable smooth scrolling (important on iOS)
        const root = document.documentElement;
        const previousScrollBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';

        // Wait for layout + async DOM mutations
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, FacetFiltersForm.savedScrollY);
            root.style.scrollBehavior = previousScrollBehavior;
          });
        });
      });
    }

    static toggleActiveFacets(disable = true) {
      document.querySelectorAll('.js-facet-remove').forEach((element) => {
        element.classList.toggle('disabled', disable);
      });
    }

    static invalidatePending() {
      FacetFiltersForm.requestVersion = (FacetFiltersForm.requestVersion || 0) + 1;
      FacetFiltersForm.pendingSubmission = false;
      FacetFiltersForm.abortController?.abort();
      return FacetFiltersForm.requestVersion;
    }

    static renderPage(searchParams, event, updateURLHash = true) {
      const requestVersion = FacetFiltersForm.invalidatePending();
      FacetFiltersForm.restoreRetryFocus = Boolean(event?.target.closest?.('[data-bt-facets-retry]'));
      // Keep the visitor's supported sorting choice when clearing filters.
      // Clear-filter links omit sort_by; an explicit value also avoids the
      // redirect fallback for collections with an unsupported admin default.
      if (window.location.pathname.includes('/collections/')) {
        const params = new URLSearchParams(searchParams);
        if (!params.has('sort_by')) {
          const currentSort = new URLSearchParams(window.location.search).get('sort_by');
          const allowedSorts = ['created-descending', 'price-ascending', 'price-descending'];
          params.set('sort_by', allowedSorts.includes(currentSort) ? currentSort : 'created-descending');
          searchParams = params.toString();
        }
      }
      FacetFiltersForm.searchParamsPrev = searchParams;
      FacetFiltersForm.abortController = new AbortController();
      const sections = FacetFiltersForm.getSections();
      const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden', 'spinning-complete'));
      document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
      document.querySelectorAll('.js-product-count').forEach((countElem) => countElem.classList.add('loading'));
      FacetFiltersForm.setFeedbackState('loading');

      // Cached responses render synchronously. Recovery and chips must see the
      // requested query before either a cached or fresh section is installed.
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

      sections.forEach((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const filterDataUrl = (element) => element.url === url;

        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : FacetFiltersForm.renderSectionFromFetch(url, event, FacetFiltersForm.abortController.signal, requestVersion);
      });

    }

    static scrollToResults() {
      // Scroll to the results
      const results = document.getElementById('product-grid').closest('.section__inner');
      if (results.getBoundingClientRect().top < 0) {
        const headerHeightValue = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
        let headerHeightNum = headerHeightValue ? parseFloat(headerHeightValue) : 0;
        window.scrollTo({
          top: results.getBoundingClientRect().top + window.scrollY - headerHeightNum,
          behavior: 'smooth'
        });
      }
    }

    static renderSectionFromFetch(url, event, signal, requestVersion = FacetFiltersForm.requestVersion) {
      fetch(url, {signal})
        .then((response) => {
          if (!response.ok) throw new Error(`Filter request failed with status ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (signal?.aborted || requestVersion !== FacetFiltersForm.requestVersion) return;
          const html = responseText;
          const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
          FacetFiltersForm.validateResponse(parsedHTML);
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {html, url}];
          const removalFocus = FacetFiltersForm.captureFacetRemovalFocus(event);
          FacetFiltersForm.renderFilters(parsedHTML, event, removalFocus);
          FacetFiltersForm.renderProductGridContainer(parsedHTML);
          FacetFiltersForm.renderProductCount(parsedHTML);
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          document.querySelectorAll('facet-filters-form custom-accordion').forEach(accordion => accordion.reinit());
          FacetFiltersForm.restoreFacetRemovalFocus(removalFocus);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || signal?.aborted || requestVersion !== FacetFiltersForm.requestVersion) return;
          console.error('[Brincatoys facets] Unable to update results.', error);
          FacetFiltersForm.resetLoadingState();
          FacetFiltersForm.setFeedbackState('error');
        });

      FacetFiltersForm.scrollToResults();
    }

    static renderSectionFromCache(filterDataUrl, event) {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      FacetFiltersForm.validateResponse(parsedHTML);
      const removalFocus = FacetFiltersForm.captureFacetRemovalFocus(event);
      FacetFiltersForm.renderFilters(parsedHTML, event, removalFocus);
      FacetFiltersForm.renderProductGridContainer(parsedHTML);
      FacetFiltersForm.renderProductCount(parsedHTML);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      window.renderSelects();
      window.loadDesktopOnlyTemplates();
      document.querySelectorAll('facet-filters-form custom-accordion').forEach(accordion => accordion.reinit());
      FacetFiltersForm.restoreFacetRemovalFocus(removalFocus);
      FacetFiltersForm.scrollToResults();
    }

    static captureFacetRemovalFocus(event) {
      const keyboardRemoval = (event?.type === 'click' && event.detail === 0)
        || (event?.type === 'keyup' && event.code?.toUpperCase() === 'SPACE');
      if (!keyboardRemoval || !event.target?.closest?.('facet-remove')) return null;

      // The visitor may have moved to another chip while the request was pending.
      const element = document.activeElement;
      if (!element?.matches?.('facet-remove a') || !element.isConnected || !element.getClientRects().length) return null;
      const surface = element.closest('.active-facets, [data-bt-missing-facets]');
      const scope = element.closest('form, #ProductGridContainer, facet-filters-form');
      const surfaceSelector = surface?.matches('.active-facets') ? '.active-facets' : '[data-bt-missing-facets]';
      const chips = surface ? Array.from(surface.querySelectorAll('facet-remove a.active-facets__button'))
        .filter(chip => chip.getClientRects().length) : [];

      return {
        element, scope, scopeId: scope?.id,
        formId: element.closest('form')?.id,
        surface, surfaceSelector,
        surfaceIndex: surface ? Array.from(scope?.querySelectorAll(surfaceSelector) || []).indexOf(surface) : -1,
        index: chips.indexOf(element),
        ageKey: element.dataset.btAgeKey,
        name: element.dataset.btFilterName,
        value: element.dataset.btFilterValue,
        href: element.getAttribute('href'),
      };
    }

    static restoreFacetRemovalFocus(state) {
      if (!state || (state.element.isConnected && state.element.getClientRects().length)) return;
      if (document.activeElement !== document.body && document.activeElement !== state.element) return;
      const visible = element => element?.isConnected && !element.disabled && element.getClientRects().length;
      const scope = state.scope?.isConnected ? state.scope : document.getElementById(state.scopeId);
      const surface = state.surface?.isConnected ? state.surface : scope?.querySelectorAll(state.surfaceSelector)[state.surfaceIndex];
      const chips = surface ? Array.from(surface.querySelectorAll('facet-remove a.active-facets__button')).filter(visible) : [];
      let target = chips.find(chip => {
        if (state.ageKey) return chip.dataset.btAgeKey === state.ageKey;
        if (state.name) return chip.dataset.btFilterName === state.name && chip.dataset.btFilterValue === state.value;
        return chip.getAttribute('href') === state.href;
      });
      if (!target && state.index >= 0) target = chips[Math.min(state.index, chips.length - 1)];

      if (!target) {
        const mobileForm = state.formId === 'FacetFiltersFormMobile' ? document.getElementById(state.formId) : null;
        const candidates = mobileForm
          ? [mobileForm.querySelector('.mobile-facets__clear'), mobileForm.querySelector('.js-apply-button'), mobileForm.querySelector('select[name="sort_by"]')]
          : [document.getElementById('Vertical-SortBy'), document.getElementById('SortBy'), document.getElementById('Desktop-SortBy'),
            ...document.querySelectorAll('#FacetFiltersForm .facets__summary, .mobile-facets__open-wrapper')];
        target = Array.from(candidates).find(visible);
      }
      target?.focus({preventScroll: true});
    }

    static renderProductGridContainer(parsedHTML) {
      document.getElementById('ProductGridContainer').innerHTML = parsedHTML
        .getElementById('ProductGridContainer').innerHTML;

      const searchCopy = document.querySelector('[data-bt-search-hero-copy]');
      const updatedCopy = parsedHTML.querySelector('[data-bt-search-hero-copy]');
      if (searchCopy && updatedCopy) {
        searchCopy.innerHTML = updatedCopy.innerHTML;
        searchCopy.dataset.resultsHeading = updatedCopy.dataset.resultsHeading;
      } else {
        const searchShortcuts = document.querySelector('[data-bt-search-shortcuts]');
        const updatedShortcuts = parsedHTML.querySelector('[data-bt-search-shortcuts]');
        if (searchShortcuts && updatedShortcuts) searchShortcuts.innerHTML = updatedShortcuts.innerHTML;
      }
      updateBrincatoysSearchRecovery();

      document
        .getElementById('ProductGridContainer')
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });

      // Both fetched and cached filters replace the pagination controls.
      const infiniteScroll = document.querySelector('infinite-scroll');
      if (typeof infiniteScroll?.destroy === 'function' && typeof infiniteScroll.init === 'function') {
        infiniteScroll.destroy();
        infiniteScroll.init();
      }
    }

    static validateResponse(parsedHTML) {
      if (!parsedHTML.getElementById('ProductGridContainer') || !parsedHTML.getElementById('product-grid')) {
        throw new Error('Filter response is missing the product grid.');
      }
    }

    static setFeedbackState(state) {
      const loading = state === 'loading';
      const failed = state === 'error';
      document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', String(loading));
      document.querySelectorAll('[data-bt-facets-feedback]').forEach((feedback) => {
        feedback.hidden = !loading && !failed;
        const message = feedback.querySelector('[data-bt-facets-message]');
        message.textContent = loading ? feedback.dataset.loadingMessage : failed ? feedback.dataset.errorMessage : '';
        feedback.querySelector('[data-bt-facets-retry]').hidden = !failed;
      });
      document.querySelectorAll('.js-apply-button').forEach((button) => {
        button.disabled = loading || failed;
        button.hidden = failed;
      });
      if (!loading && FacetFiltersForm.restoreRetryFocus) {
        const selector = failed ? '[data-bt-facets-retry]' : '.js-apply-button';
        const visibleButton = Array.from(document.querySelectorAll(selector)).find((button) => button.getClientRects().length);
        const focusTarget = visibleButton || document.getElementById('ProductGridContainer');
        if (focusTarget && !visibleButton) focusTarget.setAttribute('tabindex', '-1');
        focusTarget?.focus({preventScroll: true});
        FacetFiltersForm.restoreRetryFocus = false;
      }
    }

    static renderProductCount(parsedHTML) {
      const count = parsedHTML.querySelector('.js-product-count')?.innerHTML;
      if (count) {
        document.querySelectorAll('.js-product-count').forEach((countElem) => {
          countElem.innerHTML = count;
          countElem.classList.remove('loading');
        });
      }

      const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden', 'spinning-complete'));
      FacetFiltersForm.setFeedbackState('ready');
    }

    static resetLoadingState() {
      document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
      document.querySelectorAll('.js-product-count').forEach((countElem) => countElem.classList.remove('loading'));
      document
        .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
        .forEach((spinner) => spinner.classList.add('hidden', 'spinning-complete'));
      FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderFilters(parsedHTML, event, removalFocus) {
      rememberBrincatoysFacetLabels();
      // Capture the current focus when the response arrives, not the older input event.
      // Chip focus must be restored after ProductGridContainer is replaced too.
      const focusState = removalFocus ? null : FacetFiltersForm.captureFilterFocus();
      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
      const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

      // Remove facets that are no longer returned from the server
      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        if (!Array.from(facetDetailsElementsFromFetch).some(({id}) => currentElement.id === id)) {
          currentElement.remove();
        }
      });

      const matchesId = (element) => {
        const jsFilter = event ? event.target.closest('.js-filter') : undefined;
        return jsFilter ? element.id === jsFilter.id : false;
      };
      const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
      const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

      facetsToRender.forEach((elementToRender) => {
        const currentElement = document.getElementById(elementToRender.id);
        // Element already rendered in the DOM so just update the innerHTML
        if (currentElement) {
          const valueState = currentElement.querySelector('brincatoys-facet-values')?.captureState?.();
          const accordionContents = currentElement.querySelector('.custom-accordion__panel');
          const fetchedAccordionContents = elementToRender.querySelector('.custom-accordion__panel');
          const currentSummary = currentElement.querySelector('.facets__summary');
          const fetchedSummary = elementToRender.querySelector('.facets__summary');

          if (currentSummary && fetchedSummary) {
            currentSummary.outerHTML = fetchedSummary.outerHTML;
          }

          if (accordionContents && fetchedAccordionContents) {
            // Replace just the accordion content where possible
            accordionContents.innerHTML = fetchedAccordionContents.innerHTML;
          } else {
            currentElement.innerHTML = elementToRender.innerHTML;
          }
          currentElement.querySelector('brincatoys-facet-values')?.restoreState?.(valueState);
        } else {
          const parent = document.getElementById(elementToRender.parentElement?.id);
          if (!parent) throw new Error('Filter response is missing the facet container.');

          // Restore the server's order when filters return after an empty result.
          // Keep the anchor in the same form, including mobile sorting above facets.
          let nextSibling = elementToRender.nextElementSibling;
          while (nextSibling) {
            const nextFromDom = document.getElementById(nextSibling.id);
            if (nextSibling.matches('.js-filter') && nextFromDom?.parentElement === parent) {
              parent.insertBefore(elementToRender, nextFromDom);
              return;
            }
            nextSibling = nextSibling.nextElementSibling;
          }
          parent.insertBefore(elementToRender, parent.querySelector('.mobile-facets__footer'));
        }
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (countsToRender) {
        const closestJSFilterID = event.target.closest('.js-filter').id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        }
      }
      markBrincatoysPartialAgeGroups(document);
      renderBrincatoysMissingFacets();
      FacetFiltersForm.restoreFilterFocus(focusState);
    }

    static captureFilterFocus() {
      const element = document.activeElement;
      const facet = element?.closest?.('.js-filter');
      const form = element?.closest?.('form');
      if (!facet && !element?.closest?.('[data-bt-missing-facets]') && !['FacetFiltersForm', 'FacetFiltersFormMobile'].includes(form?.id)) return null;

      return {
        element,
        facetId: facet?.id,
        formId: form?.id,
        ageLabel: element.matches('[data-bt-age-control]') ? element.closest('[data-bt-age-group]')?.dataset.btFacetLabel : null,
        isCheckbox: element.matches('input[type="checkbox"], input[type="radio"]'),
        name: element.name,
        value: element.value,
        id: element.id,
        selectionStart: element.selectionStart,
        selectionEnd: element.selectionEnd,
      };
    }

    static restoreFilterFocus(state) {
      if (!state || (state.element.isConnected && state.element.getClientRects().length)) return;
      if (document.activeElement !== document.body && document.activeElement !== state.element) return;

      const facet = document.getElementById(state.facetId);
      let target;
      if (facet) {
        // Rendered IDs use list positions; match the selected age or value instead.
        target = Array.from(facet.querySelectorAll('input, select, button, summary')).find((element) => {
          if (state.ageLabel) return element.matches('[data-bt-age-control]') && element.closest('[data-bt-age-group]')?.dataset.btFacetLabel === state.ageLabel;
          if (state.isCheckbox) return element.name === state.name && element.value === state.value;
          return state.id && element.id === state.id;
        });
        if (!target || target.disabled || !target.getClientRects().length) target = facet.querySelector('.facets__summary');
      } else {
        target = document.getElementById(state.formId)?.querySelector('select[name="sort_by"], .facets__summary, .js-apply-button');
      }
      if (!target || target.disabled || !target.getClientRects().length) return;

      target.focus({preventScroll: true});
      if (Number.isInteger(state.selectionStart) && /^(text|search|tel|url|password)$/.test(target.type)) {
        target.setSelectionRange(state.selectionStart, state.selectionEnd);
      }
    }

    static renderActiveFacets(html) {
      const newFacets = html.querySelector('.active-facets');
      if (newFacets) {
        document.querySelectorAll('.active-facets').forEach(pageFacets => {
          pageFacets.innerHTML = newFacets.innerHTML;
        });
      }
      FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
      const mobileElementSelectors = ['.mobile-facets__open', '.js-apply-button'];

      mobileElementSelectors.forEach((selector) => {
        const sourceElements = html.querySelectorAll(selector);
        document.querySelectorAll(selector).forEach((targetElement, index) => {
          const sourceElement = sourceElements[index] || sourceElements[0];
          if (sourceElement) targetElement.innerHTML = sourceElement.innerHTML;
          if (sourceElement?.dataset?.btServerCount !== undefined) targetElement.dataset.btServerCount = sourceElement.dataset.btServerCount;
        });
      });

      html.querySelectorAll('select[name="sort_by"]').forEach((sourceSelect) => {
        const targetSelect = document.getElementById(sourceSelect.id);
        if (!targetSelect) return;
        targetSelect.innerHTML = sourceSelect.innerHTML;
        targetSelect.value = sourceSelect.value;
      });
    }

    static renderCounts(source, target) {
      const valueState = target.querySelector('brincatoys-facet-values')?.captureState?.();
      const targetSummary = target.querySelector('.facets__summary');
      const sourceSummary = source.querySelector('.facets__summary');

      if (sourceSummary && targetSummary) {
        targetSummary.outerHTML = sourceSummary.outerHTML;
      }

      const targetHeaderElement = target.querySelector('.facets__header');
      const sourceHeaderElement = source.querySelector('.facets__header');

      if (sourceHeaderElement && targetHeaderElement) {
        targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
      }

      const targetWrapElement = target.querySelector('.facets-wrap');
      const sourceWrapElement = source.querySelector('.facets-wrap');

      if (sourceWrapElement && targetWrapElement) {
        const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
        if (isShowingMore) {
          sourceWrapElement
            .querySelectorAll('.facets__item.hidden')
            .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
        }

        targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
      }
      target.querySelector('brincatoys-facet-values')?.restoreState?.(valueState);
    }

    static updateURLHash(searchParams) {
      history.pushState({searchParams}, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
      updateBrincatoysBudgetLinks();
    }

    static getSections() {
      return [
        {
          section: document.getElementById('product-grid').dataset.id,
        },
      ];
    }

    createSearchParams(forms, preferredSort, changedInput) {
      return buildBrincatoysFacetSearchParams(forms, preferredSort, window.location.search, changedInput);
    }

    onSubmitForm(searchParams, event) {
      FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
      // const noJsElements = document.querySelectorAll('.no-js-list');
      // noJsElements.forEach((el) => el.remove());

      event.preventDefault();
      const thisForm = event.target.closest('form');

      if (thisForm) {
        const forms = [];
        const isMobileForm = thisForm.id === 'FacetFiltersFormMobile';
        const isDrawer = Boolean(document.querySelector('.facets-container-drawer'));

        if (isMobileForm) {
          forms.push(thisForm);
        } else if (thisForm.id === 'FacetSortForm') {
          const desktopFiltersForm = document.getElementById('FacetFiltersForm');
          if (desktopFiltersForm) forms.push(desktopFiltersForm);
          forms.push(thisForm);
        } else if (thisForm.id === 'FacetFiltersForm' && isDrawer) {
          const drawerFiltersForm = document.getElementById('FacetFiltersFormMobile');
          if (drawerFiltersForm) forms.push(drawerFiltersForm);
          forms.push(thisForm);
        } else {
          forms.push(thisForm);
          const desktopSortForm = document.getElementById('FacetSortForm');
          if (desktopSortForm) forms.push(desktopSortForm);
        }

        const preferredSort = event.target.matches?.('select[name="sort_by"]') ? event.target.value : null;
        this.onSubmitForm(this.createSearchParams(forms, preferredSort, event.target), event);
      }
    }

    onActiveFilterClick(event) {
      event.preventDefault();
      FacetFiltersForm.toggleActiveFacets();
      const ageValues = event.currentTarget.dataset.btAgeValues;
      if (ageValues) {
        const valuesToRemove = new Set(JSON.parse(ageValues));
        const ageKey = event.currentTarget.dataset.btAgeKey;
        const searchParams = new URLSearchParams(window.location.search);
        const ageFilter = 'filter.p.product_type';
        const remainingAges = searchParams.getAll(ageFilter).filter((value) =>
          !valuesToRemove.has(value) && (!ageKey || getBrincatoysAgeKey(value) !== ageKey)
        );
        searchParams.delete(ageFilter);
        remainingAges.forEach((value) => searchParams.append(ageFilter, value));
        FacetFiltersForm.renderPage(searchParams.toString(), event);
        return;
      }
      const url =
        event.currentTarget.href.indexOf('?') == -1
          ? ''
          : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url, event);
    }
  }

  FacetFiltersForm.filterData = [];
  FacetFiltersForm.abortController = null;
  FacetFiltersForm.requestVersion = 0;
  FacetFiltersForm.pendingSubmission = false;
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.initBFCacheScrollHandling();
  FacetFiltersForm.setListeners();
}

if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.setMinAndMaxValues();
      this.updateBarFill();
      this.addEventListener('input', this.onInputUpdate.bind(this));
    }

    onInputUpdate(event) {
      if (event.detail !== undefined) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.setInputValue(event);
          this.updateBarFill();
        }, 600);
      } else {
        this.setInputValue(event);
        this.updateBarFill();
      }
    }

    setInputValue(event) {
      const editingMin = event.target === this.priceMinElem || event.target === this.minPriceRangeElem;
      const editingMax = event.target === this.priceMaxElem || event.target === this.maxPriceRangeElem;
      if (!editingMin && !editingMax) return;

      const input = editingMin ? this.priceMinElem : this.priceMaxElem;
      const slider = editingMin ? this.minPriceRangeElem : this.maxPriceRangeElem;
      const fallback = editingMin ? this.minValue : this.maxValue;
      const value = Number.parseFloat(event.target.value);

      if (!Number.isFinite(value)) {
        input.value = '';
        slider.value = String(fallback);
      } else {
        const opposite = Number.parseFloat(editingMin ? this.priceMaxElem.value : this.priceMinElem.value);
        const lower = !editingMin && Number.isFinite(opposite) ? opposite : this.minValue;
        const upper = editingMin && Number.isFinite(opposite) ? opposite : this.maxValue;
        const amount = Math.round(Math.max(lower, Math.min(upper, value)) * 100) / 100;

        // Preserve cents in both controls. Only slider endpoints clear a boundary.
        input.value = event.target === slider && amount === fallback ? '' : String(amount);
        slider.value = String(amount);
      }

      this.setMinAndMaxValues();
      this.minPriceRangeElem.value = this.priceMinElem.value || String(this.minValue);
      this.maxPriceRangeElem.value = this.priceMaxElem.value || String(this.maxValue);
    }

    updateBarFill() {
      const min = Number.parseFloat(this.priceMinElem.value);
      const max = Number.parseFloat(this.priceMaxElem.value);
      const lowerValue = Number.isFinite(min) ? min : this.minValue;
      const upperValue = Number.isFinite(max) ? max : this.maxValue;
      const percentageLower = this.maxValue > 0 ? (lowerValue / this.maxValue) * 100 : 0;
      const percentageUpper = this.maxValue > 0 ? (upperValue / this.maxValue) * 100 : 100;
      this.querySelector('.price-range').style.setProperty('--range-gap-start', `${percentageLower}%`);
      this.querySelector('.price-range').style.setProperty('--range-gap-end', `${100 - percentageUpper}%`);

      if (percentageLower === 0 && (100 - percentageUpper) === 0) {
        this.classList.add('facets__price--unset');
        this.parentElement.querySelector('facet-remove a')?.setAttribute('tabindex', '-1');
      } else {
        this.classList.remove('facets__price--unset');
        this.parentElement.querySelector('facet-remove a')?.setAttribute('tabindex', '0');
      }
    }

    setMinAndMaxValues() {
      this.priceMinElem = this.querySelector('.js-price-min');
      this.priceMaxElem = this.querySelector('.js-price-max');
      this.minPriceRangeElem = this.querySelector('.js-price-range-min');
      this.maxPriceRangeElem = this.querySelector('.js-price-range-max');

      this.minValue = Number.parseFloat(this.priceMinElem.min);
      this.maxValue = Number.parseFloat(this.priceMaxElem.max);

      if (this.priceMaxElem.value) this.priceMinElem.setAttribute('max', this.priceMaxElem.value);
      if (this.priceMinElem.value) this.priceMaxElem.setAttribute('min', this.priceMinElem.value);
      if (this.priceMinElem.value === '') this.priceMaxElem.setAttribute('min', this.minValue);
      if (this.priceMaxElem.value === '') this.priceMinElem.setAttribute('max', this.maxValue);
    }
  }

  customElements.define('price-range', PriceRange);
}

if (!customElements.get('facet-remove')) {
  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      const facetLink = this.querySelector('a');
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }

    closeFilter(event) {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    }
  }

  customElements.define('facet-remove', FacetRemove);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateBrincatoysBudgetLinks, { once: true });
  document.addEventListener('DOMContentLoaded', renderBrincatoysMissingFacets, { once: true });
} else {
  updateBrincatoysBudgetLinks();
  renderBrincatoysMissingFacets();
}
window.addEventListener('pageshow', updateBrincatoysBudgetLinks);
window.addEventListener('pageshow', renderBrincatoysMissingFacets);
document.addEventListener('shopify:section:load', updateBrincatoysBudgetLinks);
document.addEventListener('shopify:section:load', renderBrincatoysMissingFacets);

(() => {
  const disclosureSelector = '.bt-collection-facets .mobile-facets__disclosure';
  const bannerSelector = '#shopify-pc__banner';
  const reserveProperty = '--bt-mobile-facets-consent-bottom';
  const root = document.documentElement;
  let currentBanner = null;
  let animationFrame = 0;
  let bannerObserver = null;
  let bannerResizeObserver = null;

  const clearReserve = () => root.style.removeProperty(reserveProperty);

  const updateReserve = () => {
    animationFrame = 0;
    const isMobile = window.matchMedia
      ? window.matchMedia('(max-width: 749px)').matches
      : window.innerWidth < 750;
    const disclosure = document.querySelector(`${disclosureSelector}[open]`);
    const banner = document.querySelector(bannerSelector);

    if (!isMobile || !disclosure || !banner) {
      clearReserve();
      return;
    }

    const style = window.getComputedStyle(banner);
    const rect = banner.getBoundingClientRect();
    if (style.display === 'none' || style.visibility === 'hidden') {
      clearReserve();
      return;
    }

    const reserve = getBrincatoysMobileFacetConsentReserve(rect, window.innerHeight);
    if (reserve > 0) root.style.setProperty(reserveProperty, `${reserve}px`);
    else clearReserve();
  };

  const scheduleUpdate = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(updateReserve);
  };

  const observeBanner = () => {
    const banner = document.querySelector(bannerSelector);
    if (banner === currentBanner) {
      scheduleUpdate();
      return;
    }

    bannerObserver?.disconnect();
    bannerResizeObserver?.disconnect();
    currentBanner = banner;

    if (banner) {
      bannerObserver = new MutationObserver(scheduleUpdate);
      bannerObserver.observe(banner, { attributes: true, attributeFilter: ['class', 'style'], childList: true, subtree: true });

      if (window.ResizeObserver) {
        bannerResizeObserver = new ResizeObserver(scheduleUpdate);
        bannerResizeObserver.observe(banner);
      }
    }

    scheduleUpdate();
  };

  if (!document.querySelector('.bt-collection-facets')) return;

  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.(disclosureSelector)) scheduleUpdate();
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.(`${bannerSelector} button`)) return;
    window.setTimeout(scheduleUpdate, 0);
    window.setTimeout(scheduleUpdate, 180);
  }, true);

  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });

  const bannerPresenceObserver = new MutationObserver(observeBanner);
  bannerPresenceObserver.observe(document.body, { childList: true });
  observeBanner();
})();
